import { useContext, useState } from 'react'
import {
    Space,
    Table,
    Typography,
    Button,
    Modal,
    Form,
    InputNumber,
    Input,
    message,
} from 'antd'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { AppContext } from '../../context/AppContext'
import {
    getCustomerLoyaltyHistory,
    adjustCustomerLoyaltyPoints,
} from '../../network/Customers'
import Loader from '../../components/Loader'
import Error from '../../components/Error'
import StatCard from '../../components/StatCard'
import DynamicStrings from '../../components/DynamicStrings'
import { formatDate, formatNb } from '../../utils/utils'

const { Title, Link } = Typography

const PAGE_SIZE = 20

const CustomersReward = ({ userId }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const { token } = useContext(AppContext)
    const queryClient = useQueryClient()
    const [form] = Form.useForm()
    const [modalOpen, setModalOpen] = useState(false)
    const [adjustMode, setAdjustMode] = useState('grant')

    const loyalty = useQuery(
        [
            'getCustomerLoyaltyHistory',
            {
                userId,
                token: token.token,
            },
        ],
        getCustomerLoyaltyHistory
    )

    const adjustMutation = useMutation(
        ({ amount, comment }) =>
            adjustCustomerLoyaltyPoints(
                userId,
                { amount, comment },
                token.token
            ),
        {
            onSuccess: () => {
                message.success(t('LOYALTY_ADJUST_SUCCESS'))
                queryClient.invalidateQueries('getCustomerLoyaltyHistory')
                setModalOpen(false)
                form.resetFields()
            },
            onError: () => message.error(t('LOYALTY_ADJUST_ERROR')),
        }
    )

    const openModal = (mode) => {
        setAdjustMode(mode)
        form.resetFields()
        setModalOpen(true)
    }

    const handleAdjust = () => {
        form.validateFields().then(({ amount, comment }) => {
            if (!amount || amount <= 0) return

            const signedAmount =
                adjustMode === 'remove' ? -Math.abs(amount) : Math.abs(amount)
            adjustMutation.mutate({ amount: signedAmount, comment })
        })
    }

    const formatType = (type) => {
        const key = `LOYALTY_TYPE_${type}`
        const label = t(key)
        return label !== key ? label : type
    }

    const formatAmount = (amount) => {
        if (amount == null) return '-'
        const prefix = amount > 0 ? '+' : ''
        return prefix + formatNb(amount)
    }

    const goToOrder = (orderId) => {
        history.push({
            pathname: `/customers/${userId}`,
            defaultTab: 'orders',
            search: `?filter=${encodeURIComponent(
                JSON.stringify({
                    orderID: { $regex: orderId, $options: 'i' },
                })
            )}`,
        })
    }

    const columns = [
        {
            title: <DynamicStrings id="order_date" />,
            dataIndex: 'date',
            key: 'date',
            render: (date) => formatDate(date, 'long'),
        },
        {
            title: <DynamicStrings id="LOYALTY_TYPE" />,
            dataIndex: 'type',
            key: 'type',
            render: (type) => formatType(type),
        },
        {
            title: <DynamicStrings id="OFFER_LIVE_ORDER_ID" />,
            dataIndex: 'order',
            key: 'order',
            render: (order, record) =>
                record.type === 'order_gain' || record.type === 'order_refund' || record.type === 'order_cancel' && order ? (
                    <Link underline onClick={() => goToOrder(order)}>
                        {order}
                    </Link>
                ) : (
                    '-'
                ),
        },
        {
            title: <DynamicStrings id="LOYALTY_AMOUNT" />,
            dataIndex: 'amount',
            key: 'amount',
            render: (amount) => formatAmount(amount),
        },
        {
            title: <DynamicStrings id="LOYALTY_NEW_BALANCE" />,
            dataIndex: 'newAmount',
            key: 'newAmount',
            render: (newAmount) =>
                newAmount != null ? formatNb(newAmount) : '-',
        },
        {
            title: <DynamicStrings id="LOYALTY_GRANTED_BY" />,
            dataIndex: 'grantedBy',
            key: 'grantedBy',
            render: (grantedBy) => grantedBy?.name || '-',
        },
        {
            title: <DynamicStrings id="LOYALTY_COMMENT" />,
            dataIndex: 'comment',
            key: 'comment',
            ellipsis: true,
            render: (comment) => comment || '-',
        },
    ]

    if (loyalty.isError) {
        return <Error retry={() => loyalty.refetch()} />
    }

    if (loyalty.isLoading) {
        return <Loader />
    }

    return (
        <Space
            direction="vertical"
            size={24}
            style={{ width: '100%', padding: '24px 24px 50px' }}
        >
            <Space align="start" size={24}>
                <StatCard
                    title={<DynamicStrings id="LOYALTY_POINTS" />}
                    value={loyalty.data?.loyaltyPoint ?? 0}
                />
                <Space>
                    <Button type="primary" onClick={() => openModal('grant')}>
                        {t('LOYALTY_GRANT_POINTS')}
                    </Button>
                    <Button danger onClick={() => openModal('remove')}>
                        {t('LOYALTY_REMOVE_POINTS')}
                    </Button>
                </Space>
            </Space>
            <div>
                <Title level={5} style={{ marginBottom: 16 }}>
                    {t('LOYALTY_HISTORY')}
                </Title>
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={loyalty.data?.history || []}
                    pagination={{
                        pageSize: PAGE_SIZE,
                        total: loyalty.data?.count,
                        showSizeChanger: false,
                    }}
                />
            </div>
            <Modal
                centered
                title={
                    adjustMode === 'grant'
                        ? t('LOYALTY_GRANT_POINTS')
                        : t('LOYALTY_REMOVE_POINTS')
                }
                visible={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={handleAdjust}
                confirmLoading={adjustMutation.isLoading}
                okText={t('OFFER_LIVE_YES')}
                cancelText={t('OFFER_LIVE_NO')}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="amount"
                        label={t('LOYALTY_AMOUNT')}
                        rules={[
                            {
                                required: true,
                                message: t('LOYALTY_AMOUNT_REQUIRED'),
                            },
                            {
                                validator: (_, value) =>
                                    value > 0
                                        ? Promise.resolve()
                                        : Promise.reject(
                                              new Error(
                                                  t('LOYALTY_AMOUNT_REQUIRED')
                                              )
                                          ),
                            },
                        ]}
                    >
                        <InputNumber
                            min={1}
                            precision={0}
                            step={1}
                            style={{ width: '100%' }}
                            placeholder={t('LOYALTY_AMOUNT')}
                            parser={(value) => {
                                const digits = value?.replace(/\D/g, '')
                                if (!digits) return ''
                                const num = parseInt(digits, 10)
                                return num >= 1 ? num : ''
                            }}
                            onKeyDown={(e) => {
                                if (
                                    e.key === '-' ||
                                    e.key === '+' ||
                                    e.key === 'e' ||
                                    e.key === 'E' ||
                                    e.key === '.'
                                ) {
                                    e.preventDefault()
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="comment" label={t('LOYALTY_COMMENT')}>
                        <Input.TextArea
                            rows={3}
                            placeholder={t('LOYALTY_COMMENT_OPTIONAL')}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </Space>
    )
}

export default CustomersReward
